import type { TaktEntry, TaktSection } from "../../models";

/**
 * Clean, human date range — collapses repetition: same month → "Jun 11 – 17,
 * 2026"; same year → "Jun 28 – Jul 3, 2026"; otherwise spells out both years.
 */
export function fmtDateRange(startIso: string, finishIso: string): string {
  if (!startIso || !finishIso) return "";
  const s = new Date(`${startIso}T00:00:00`);
  const f = new Date(`${finishIso}T00:00:00`);
  const sMon = s.toLocaleDateString("en-US", { month: "short" });
  const fMon = f.toLocaleDateString("en-US", { month: "short" });
  const sY = s.getFullYear();
  const fY = f.getFullYear();
  if (sY === fY && sMon === fMon) return `${sMon} ${s.getDate()} – ${f.getDate()}, ${fY}`;
  if (sY === fY) return `${sMon} ${s.getDate()} – ${fMon} ${f.getDate()}, ${fY}`;
  return `${sMon} ${s.getDate()}, ${sY} – ${fMon} ${f.getDate()}, ${fY}`;
}

/** Sanitize a work code for use as a CSS class suffix. */
export function sanitizeWorkCode(code: string): string {
  return code.replace(/[^a-zA-Z0-9_-]/g, "_");
}

// Approx px-per-char for the bar-label font; CSS ellipsis is the truncation +
// overflow safety net. PAD = the bar's horizontal padding (6px each side for a
// left label; a centered code can run tighter).
const LABEL_PER_CHAR = 6.6;
const LABEL_PAD = 12;
const CODE_PAD = 4;
// Show at least this much of the descriptive name before falling back to the
// code — fewer chars than this and a truncated preview isn't worth it.
const MIN_LABEL_CHARS = 7;

export interface BarLabel {
  text: string;
  align: "left" | "center";
  /** Work code rendered as a bold prefix ahead of `text` (the pull-plan look);
   *  unset for the code-only fallback and when the name already is the code. */
  code?: string;
}

/**
 * The bar-label ladder, preferring the descriptive `label`: show the label
 * (TRUNCATED by CSS ellipsis to whatever fits) whenever a meaningful chunk fits,
 * prefixed by the work code; otherwise fall back to the short `work_code` — in
 * full if it fits, else as many leading characters as do. A couple of code chars
 * beat a blank bar; only a bar with no room for even one character gets nothing
 * (the inspector covers it on hover).
 */
export function chooseBarLabel(
  label: string,
  workCode: string,
  barPx: number,
): BarLabel | null {
  const labelChars = Math.floor((barPx - LABEL_PAD) / LABEL_PER_CHAR);
  if (labelChars >= Math.min(label.length, MIN_LABEL_CHARS)) {
    return label === workCode
      ? { text: label, align: "left" }
      : { text: label, align: "left", code: workCode };
  }
  const codeChars = Math.floor((barPx - CODE_PAD) / LABEL_PER_CHAR);
  if (codeChars >= 1) {
    const text =
      codeChars >= workCode.length ? workCode : workCode.slice(0, codeChars);
    return { text, align: "center" };
  }
  return null;
}

// Narrowest bar that still earns a trade label; below this, color alone carries
// the trade and CSS ellipsis would leave nothing readable.
const MIN_TRADE_LABEL_PX = 24;

/**
 * The bar label when coloring by trade: the trade name, left-aligned and left
 * to CSS ellipsis for truncation (there's no short-code fallback for a trade).
 */
export function chooseTradeLabel(
  tradeName: string,
  barPx: number,
): BarLabel | null {
  if (!tradeName || barPx < MIN_TRADE_LABEL_PX) return null;
  return { text: tradeName, align: "left" };
}

/**
 * Bin-pack entries into rows so non-overlapping entries share a row.
 * Preserves backend sort order (SortA → SortB → SortC → work code) for
 * stable row assignment — shifting dates won't swap rows.
 */
export function binPackEntries(entries: TaktEntry[]): TaktEntry[][] {
  if (entries.length === 0) return [];

  const slots: TaktEntry[][] = [];

  for (const entry of entries) {
    let placed = false;
    for (const slot of slots) {
      const last = slot[slot.length - 1];
      if (last.finish < entry.start) {
        slot.push(entry);
        placed = true;
        break;
      }
    }
    if (!placed) {
      slots.push([entry]);
    }
  }

  return slots;
}

/** Collect unique work codes for a section, sorted alpha-first then numeric. */
export function getSectionCodes(section: TaktSection): Array<[string, string]> {
  const map = new Map<string, string>();
  for (const g of section.groups) {
    for (const e of g.entries) {
      if (!map.has(e.work_code)) map.set(e.work_code, e.label);
    }
  }
  return Array.from(map.entries()).sort((a, b) => {
    const aNum = /^\d+$/.test(a[0]);
    const bNum = /^\d+$/.test(b[0]);
    if (aNum && bNum) return parseInt(a[0]) - parseInt(b[0]);
    if (!aNum && !bNum) return a[0].localeCompare(b[0]);
    return aNum ? 1 : -1;
  });
}
